import React, { useRef, useState, useEffect } from 'react';
import { SITE_CONTENT } from '../content';

const SocialIcon = ({ href, children }) => (
  <a
    href={href}
    className="w-8 h-8 rounded-full bg-[#f0eee5] flex items-center justify-center text-[#1a2e1a] hover:bg-[#1a2e1a] hover:text-[#f0eee5] transition-colors duration-300 transform hover:scale-110"
  >
    {children}
  </a>
);

// Fallback SVGs
const FacebookIcon = () => (
  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
  </svg>
);

const TwitterIcon = () => (
  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
  </svg>
);

const LinkedInIcon = () => (
  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" />
  </svg>
);

const OurTeam = () => {
  const team = SITE_CONTENT.pages?.aboutUsPage?.ourTeam || {};
  const subtitle = team.subtitle || "Our Chefs";
  const title = team.title || "Masters of Authentic Cuisine";
  const teamMembers = team.teamMembers || [];
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 bg-[#f0eee5]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        
        {/* Section Header */}
        <div className="text-center mb-14">
          <p className="text-[#1a2e1a] text-xs font-semibold tracking-[0.25em] uppercase mb-4">
            {subtitle}
          </p>
          <h2 className="text-[#1a2e1a] text-3xl sm:text-4xl font-semibold">
            {title}
          </h2>
        </div>

        {/* Team Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {teamMembers.map((member, idx) => (
            <div
              key={member.name}
              className={`group flex flex-col transition-all duration-700 ease-out ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${idx * 150}ms` }}
            >
              {/* Image Container with Hover Overlay */}
              <div className="relative overflow-hidden mb-6 bg-gray-200 aspect-square sm:aspect-auto sm:h-80 w-full shadow-sm">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover transition-transform duration-700 ease-in-out group-hover:scale-105"
                />
                
                {/* Social Overlay */}
                <div className="absolute inset-0 bg-[#1a2e1a]/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-3">
                  <div className="translate-y-4 group-hover:translate-y-0 transition-transform duration-300 delay-75">
                    <SocialIcon href={member.socials.facebook}>
                      <FacebookIcon />
                    </SocialIcon>
                  </div>
                  <div className="translate-y-4 group-hover:translate-y-0 transition-transform duration-300 delay-100">
                    <SocialIcon href={member.socials.twitter}>
                      <TwitterIcon />
                    </SocialIcon>
                  </div>
                  <div className="translate-y-4 group-hover:translate-y-0 transition-transform duration-300 delay-150">
                    <SocialIcon href={member.socials.linkedin}>
                      <LinkedInIcon />
                    </SocialIcon>
                  </div>
                </div>
              </div>

              {/* Text Content */}
              <div>
                <h3 className="text-[#1a2e1a] text-lg font-semibold mb-1">
                  {member.name}
                </h3>
                <p className="text-[#7a7a68] text-[10px] tracking-[0.2em] font-bold uppercase">
                  {member.role}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default OurTeam;
